import { designTokens } from "@amplecare/design-tokens";
import { translationKeys, type TranslationKey } from "@amplecare/i18n";

import type { ChatMessageViewModel } from "./types.js";

export type AppShellTheme =
  | (typeof designTokens.semanticTheme)["light"]
  | (typeof designTokens.semanticTheme)["dark"]
  | (typeof designTokens.semanticTheme)["workforceWarm"]
  | (typeof designTokens.semanticTheme)["workforceWarmth"]
  | (typeof designTokens.semanticTheme)["workforceBlueGrey"]
  | (typeof designTokens.semanticTheme)["clinicianWorkbench"]
  | (typeof designTokens.semanticTheme)["clinicianSlate"];

export type LegacyDetailPreviewTone =
  | "neutral"
  | "accent"
  | "warning"
  | "info";

export type LegacySectionChromeTone =
  | "neutral"
  | "accent"
  | "warning"
  | "info";

export const patientMobilePalette =
  designTokens.semanticTheme.patientMobileWorkspace;
export const patientDesktopPalette =
  designTokens.semanticTheme.patientDesktopWorkspace;

export function resolvePatientInitials(displayName: string): string {
  const parts = displayName
    .trim()
    .split(/\s+/u)
    .filter((part) => part.length > 0);

  if (parts.length === 0) {
    return "PT";
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] ?? ""}${parts[parts.length - 1]?.[0] ?? ""}`.toUpperCase();
}

export function resolvePatientNameLines(displayName: string): {
  primaryText: string;
  secondaryText: string | null;
} {
  const parts = displayName
    .trim()
    .split(/\s+/u)
    .filter((part) => part.length > 0);

  if (parts.length === 0) {
    return {
      primaryText: "Patient",
      secondaryText: null,
    };
  }

  if (parts.length === 1) {
    return {
      primaryText: parts[0],
      secondaryText: null,
    };
  }

  return {
    primaryText: parts[parts.length - 1] ?? parts[0],
    secondaryText: parts.slice(0, -1).join(" "),
  };
}

export function resolveLegacyNameplate(displayName: string): string {
  const { primaryText, secondaryText } = resolvePatientNameLines(displayName);

  if (!secondaryText) {
    return primaryText;
  }

  return `${primaryText}, ${secondaryText}`;
}

export function resolveLegacyPreviewFallbackSecondaryText(
  labelText: string,
): string {
  const significantPart =
    labelText
      .trim()
      .split(/\s+/u)
      .map((part) => part.toLowerCase())
      .find((part) => part !== "upcoming" && part !== "next") ?? "summary";

  return significantPart.slice(0, 7);
}

export function resolveLegacyPreviewFallbackPrimaryText(
  labelText: string,
): string {
  const significantPart =
    labelText
      .trim()
      .split(/\s+/u)
      .map((part) => part.toLowerCase())
      .find((part) => part !== "upcoming" && part !== "next") ?? "info";

  return significantPart.slice(0, 4).toUpperCase();
}

export function getLegacyDetailPreviewPalette(
  tone: LegacyDetailPreviewTone,
  theme: AppShellTheme,
) {
  if (tone === "accent") {
    return {
      backgroundColor: theme.accentSubtle,
      borderColor: theme.borderSubtle,
      leadingBackgroundColor: theme.surface,
      leadingTextColor: theme.accentStrong,
      groupLabelColor: theme.accentStrong,
    };
  }

  if (tone === "warning") {
    return {
      backgroundColor: theme.surfaceMuted,
      borderColor: theme.borderSubtle,
      leadingBackgroundColor: theme.surface,
      leadingTextColor: theme.warning,
      groupLabelColor: theme.warning,
    };
  }

  if (tone === "info") {
    return {
      backgroundColor: theme.surfaceMuted,
      borderColor: theme.borderSubtle,
      leadingBackgroundColor: theme.surface,
      leadingTextColor: theme.info,
      groupLabelColor: theme.info,
    };
  }

  return {
    backgroundColor: theme.surfaceMuted,
    borderColor: theme.borderSubtle,
    leadingBackgroundColor: theme.surface,
    leadingTextColor: theme.textPrimary,
    groupLabelColor: theme.textSecondary,
  };
}

export function humanizeWorkspaceKey(value: string): string {
  return value
    .trim()
    .split(/[-_\s]+/u)
    .filter((part) => part.length > 0)
    .map((part) => `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}`)
    .join(" ");
}

export function resolveWorkforceSessionRoleLabel(
  roleKeys: readonly string[],
): string {
  if (roleKeys.includes("patient")) {
    return "Patient";
  }

  if (
    roleKeys.some((roleKey) =>
      [
        "tenant-admin",
        "site-manager",
        "department-manager",
        "program-manager",
        "care-team-lead",
      ].includes(roleKey),
    )
  ) {
    return "Admin";
  }

  if (roleKeys.includes("clinician")) {
    return "Clinician";
  }

  if (roleKeys.length === 0) {
    return "Workspace";
  }

  return humanizeWorkspaceKey(roleKeys[0] ?? "workspace");
}

export function resolvePatientAccessModeLabel(value: string): string {
  return humanizeWorkspaceKey(value.replace(/-panel$/u, " panel"));
}

export function resolveMessageDeliveryLabelKey(
  deliveryState: ChatMessageViewModel["deliveryState"],
): TranslationKey | null {
  if (deliveryState === "sent") {
    return translationKeys.shell.workspaceChatDeliverySent;
  }

  if (deliveryState === "delivered") {
    return translationKeys.shell.workspaceChatDeliveryDelivered;
  }

  return null;
}

export function resolveLegacyCompactLeadText(
  value: string,
  fallback = "ITEM",
): string {
  const parts = value
    .trim()
    .split(/[\s|/_-]+/u)
    .filter((part) => part.length > 0);

  if (parts.length === 0) {
    return fallback;
  }

  if (parts.length === 1) {
    return parts[0]!.slice(0, 4).toUpperCase();
  }

  return parts
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 4)
    .toUpperCase();
}

export function getLegacySectionChromeColors(
  _tone: LegacySectionChromeTone,
  isSelected: boolean,
  theme: AppShellTheme,
) {
  return {
    backgroundColor: isSelected ? theme.backgroundSubtle : "transparent",
    borderColor: "transparent",
    titleColor: isSelected ? theme.accentStrong : theme.textPrimary,
    supportColor: theme.textSecondary,
    leadBackgroundColor: "transparent",
    leadBorderColor: "transparent",
    leadTextColor: isSelected ? theme.accentStrong : theme.textPrimary,
    countBackgroundColor: isSelected
      ? theme.accentSubtle
      : theme.borderSubtle,
    countBorderColor: "transparent",
    countTextColor: isSelected ? theme.accentStrong : theme.textSecondary,
  };
}
